import { FC, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import { pluralize } from 'further-ui/utils';
import PayablesOrReceivables from './PayablesOrReceivables';
import Cash from './Cash';
import ProcessingPage from 'components/new/ProcessingPage';
import TabsPanel from 'further-ui/components/TabsPanel';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { styled } from '@mui/material/styles';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import api from 'lib/trpcClient';
import { useDisclosure } from 'further-ui/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column, Row } from 'further-ui/components/Layout';
import PayablesOrReceivablesHistoryDetail from './PayablesOrReceivablesHistoryDetail';
import CashHistoryDetailEmbedded from './CashHistoryDetailEmbedded';
import Chip from 'components/Chip';
import { type Transaction } from 'components/TransactionAllocator';
import usePostUnrealisedFxGainsData from './usePostUnrealisedFxGainsData';
import {
  PostUnrealisedFxGainsProvider,
  usePostUnrealisedFxGainsContext,
} from './PostUnrealisedFxGainsContext';
import { useLayoutContext } from 'contexts/LayoutContext';

enum PostUnrealisedFXGainsTab {
  PayablesOrReceivables = 'payablesOrReceivables',
  Cash = 'cash',
}

const tabs = [
  {
    key: PostUnrealisedFXGainsTab.PayablesOrReceivables,
    title: 'Payables or receivables',
  },
  { key: PostUnrealisedFXGainsTab.Cash, title: 'Cash' },
];

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

type Props = {
  onExit: () => void;
};

const PostUnrealisedFXGainsContent: FC<Props> = ({ onExit }) => {
  const { vehicle, selectedVehicleId } = useLayoutContext();
  const {
    periodId,
    periodEnd,
    isConfirmed,
    payablesData,
    payablesIsFetching,
    cashData,
    cashIsFetching,
    payablesCurrencyRates,
    cashCurrencyRates,
    confirmedPayablesData,
    confirmedCashData,
  } = usePostUnrealisedFxGainsData();
  const { currencyRateOverrides, setCurrencyRateOverrides } =
    usePostUnrealisedFxGainsContext();

  const [canCreate] = useHasPermissions([
    RolePermission.LpCreateUnrealisedFxGainOrLoss,
  ]);
  const [canUnapprove] = useHasPermissions([
    RolePermission.LpEditAccountingPeriod,
  ]);
  const { search } = useLocation();
  const history = useHistory();

  const [payablesTransactionChanges, setPayablesTransactionChanges] = useState<
    Map<string, Array<Transaction>>
  >(new Map());
  const [cashTransactionChanges, setCashTransactionChanges] = useState<
    Map<string, Array<Transaction>>
  >(new Map());

  const searchParams = new URLSearchParams(search);
  const selectedRecordId = searchParams.get('selectedRecordId');
  const setSelectedRecordId = (id: string | null) => {
    const params = new URLSearchParams(search);
    if (id) {
      params.set('selectedRecordId', id);
    } else {
      params.delete('selectedRecordId');
    }
    history.push({ search: params.toString() });
  };

  const selectedCashRecordId = searchParams.get('selectedCashRecordId');
  const setSelectedCashRecordId = (id: string | null) => {
    const params = new URLSearchParams(search);
    if (id) {
      params.set('selectedCashRecordId', id);
    } else {
      params.delete('selectedCashRecordId');
    }
    history.push({ search: params.toString() });
  };

  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [isProcessingUnapproval, setIsProcessingUnapproval] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isUnapproveModalOpen, setIsUnapproveModalOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const { hasEqualisationInDraftState } = useEqualisations(
    selectedVehicleId ?? '',
  );
  const confirmDialog = useDisclosure();

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: createUnrealisedGains } =
    api.accountingPeriod.createUnrealisedGainsForPeriodEnd.useMutation(
      mutationOpts({
        successMessage:
          'Unrealised FX gains/losses posted and approved successfully.',
        refetchType: 'active',
        onSuccess: () =>
          history.push(createLpPeriodEndReviewDetailRoute(periodId ?? '')),
      }),
    );

  const { mutateAsync: unconfirmUnrealisedFxGains } =
    api.accountingPeriod.unconfirmChecklistItem.useMutation(
      mutationOpts({
        successMessage: 'Unrealised FX gains unapproved successfully.',
        refetchType: 'active',
        onSuccess: () =>
          history.push(createLpPeriodEndReviewDetailRoute(periodId ?? '')),
      }),
    );

  const handleApproveClick = () => {
    setIsApproveModalOpen(true);
  };

  const handleApproveConfirm = () => {
    setIsApproveModalOpen(false);
    if (hasEqualisationInDraftState) {
      confirmDialog.onOpen();
    } else {
      void handleApprove();
    }
  };

  const handleUnapprove = async () => {
    if (!selectedVehicleId || !periodId) return;
    setIsUnapproveModalOpen(false);
    setIsProcessingUnapproval(true);
    try {
      await unconfirmUnrealisedFxGains({
        vehicleId: selectedVehicleId,
        id: periodId,
        item: 'unrealisedFxGainsAndLosses',
      });
    } finally {
      setIsProcessingUnapproval(false);
    }
  };

  const handlePayablesTransactionChange = (
    rowId: string,
    transactions: Array<Transaction>,
  ) => {
    setPayablesTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, transactions);
      return newMap;
    });
  };

  const handleCashTransactionChange = (
    rowId: string,
    transactions: Array<Transaction>,
  ) => {
    setCashTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, transactions);
      return newMap;
    });
  };

  const handleApprove = async () => {
    setIsPending(true);
    setIsProcessingApproval(true);
    try {
      if (!selectedVehicleId || !periodId) {
        throw new Error('Invalid vehicle or period ID');
      }

      const payablesBody = (payablesData?.transactions.results ?? []).map(
        (row) => {
          const transactions = payablesTransactionChanges.get(row.id);
          return {
            sourceTransactionId: row.id,
            transactions: (transactions ?? row.transactions).map((t) => ({
              transactionTypeId: t.transactionTypeId,
              operationType: t.operationType,
            })),
          };
        },
      );

      const cashBody = (cashData?.results ?? []).map((row) => {
        const transactions = cashTransactionChanges.get(row.id);
        return {
          bankAccountId: row.id,
          transactions: (transactions ?? row.transactions).map((t) => ({
            transactionTypeId: t.transactionTypeId,
            operationType: t.operationType,
          })),
        };
      });

      await createUnrealisedGains({
        vehicleId: vehicle?._id ?? '',
        periodId,
        ...(payablesBody.length > 0 && {
          fxGains: {
            transactions: payablesBody,
            currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
          },
        }),
        ...(cashBody.length > 0 && {
          cashFxGains: {
            bankAccounts: cashBody,
            currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
          },
        }),
      });
    } catch {
      setIsProcessingApproval(false);
    } finally {
      setIsPending(false);
    }
  };

  if (!vehicle) {
    return null;
  }

  const handleExit = () => {
    onExit();
  };

  const currencyRates = payablesCurrencyRates.length
    ? payablesCurrencyRates
    : cashCurrencyRates;

  const payablesCount = isConfirmed
    ? confirmedPayablesData?.totalCount ?? 0
    : payablesData?.transactions.totalCount ?? 0;
  const cashCount = isConfirmed
    ? confirmedCashData?.totalCount ?? 0
    : cashData?.results?.length ?? 0;

  const tabCounts = {
    [PostUnrealisedFXGainsTab.PayablesOrReceivables]: payablesCount,
    [PostUnrealisedFXGainsTab.Cash]: cashCount,
  };
  const totalTransactionCount = payablesCount + cashCount;

  const tabLoading = {
    [PostUnrealisedFXGainsTab.PayablesOrReceivables]: payablesIsFetching,
    [PostUnrealisedFXGainsTab.Cash]: cashIsFetching,
  };

  const currencyRateJsx =
    !isConfirmed && currencyRates.length > 0 ? (
      <Box>
        <Typography variant="h6" color="text.secondary">
          {pluralize('Transaction currency rate', currencyRates.length ?? 0)}:
        </Typography>
        {currencyRates.map(([currencyCode, fxRate], index) => (
          <CurrencyRate
            key={`${currencyCode}-${index}`}
            fxRate={fxRate}
            date={periodEnd}
            currency={currencyCode}
            vehicle={vehicle}
            isFetchingRate={false}
            onRateChange={(rateDetails) => {
              setCurrencyRateOverrides((prev) => {
                const newMap = new Map(prev);
                newMap.set(currencyCode, rateDetails);
                return newMap;
              });
              setPayablesTransactionChanges(new Map());
              setCashTransactionChanges(new Map());
            }}
          />
        ))}
      </Box>
    ) : null;

  return (
    <FullPageDrawer
      title={
        selectedRecordId
          ? 'Unrealised FX Gain/Loss Details'
          : selectedCashRecordId
          ? 'Unrealised Cash FX Gain/Loss Details'
          : 'Approve unrealised FX gains and losses'
      }
      titleAdornment={
        !selectedRecordId && !selectedCashRecordId ? (
          <Tooltip title="Transactions will be posted to the GL upon approval">
            <Typography component="span">⚡</Typography>
          </Tooltip>
        ) : undefined
      }
      open
      innerScroll
      size="lg"
      onClose={handleExit}
      actions={
        <Row fullWidth justify="space-between">
          <Button
            size="large"
            variant="outlined"
            onClick={
              selectedRecordId
                ? () => setSelectedRecordId(null)
                : selectedCashRecordId
                ? () => setSelectedCashRecordId(null)
                : handleExit
            }
          >
            {selectedRecordId || selectedCashRecordId ? 'Back' : 'Exit'}
          </Button>
          {!selectedRecordId && !selectedCashRecordId && (
            <Row spacing="sm">
              {isConfirmed && canUnapprove && (
                <Button
                  size="large"
                  variant="outlined"
                  color="error"
                  onClick={() => setIsUnapproveModalOpen(true)}
                >
                  Unapprove and delete transactions
                </Button>
              )}
              {!isConfirmed && canCreate && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleApproveClick}
                  disabled={isPending}
                >
                  Approve and create transactions
                </Button>
              )}
            </Row>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {selectedRecordId ? (
          <PayablesOrReceivablesHistoryDetail recordId={selectedRecordId} />
        ) : selectedCashRecordId ? (
          <CashHistoryDetailEmbedded recordId={selectedCashRecordId} />
        ) : isProcessingApproval ? (
          <ProcessingPage
            title="Approving transactions"
            subtitle="This will only take a moment."
          />
        ) : isProcessingUnapproval ? (
          <ProcessingPage
            title="Removing unrealised FX gain/loss entries"
            subtitle="This will only take a moment."
          />
        ) : (
          <TabsPanel
            keepAlive
            tabs={tabs.map(({ key, title }) => ({
              key,
              label: (
                <TabLabelWrapper>
                  {title}
                  {tabLoading[key] ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Chip
                      variant="tabIndicator"
                      small
                      bold={false}
                      empty={tabCounts[key] === 0 || isConfirmed}
                    >
                      {tabCounts[key]}
                    </Chip>
                  )}
                </TabLabelWrapper>
              ),
              content:
                key === PostUnrealisedFXGainsTab.PayablesOrReceivables ? (
                  <>
                    {currencyRateJsx}
                    <PayablesOrReceivables
                      transactionChanges={payablesTransactionChanges}
                      onTransactionChange={handlePayablesTransactionChange}
                      onViewRecord={setSelectedRecordId}
                    />
                  </>
                ) : (
                  <>
                    {currencyRateJsx}
                    <Cash
                      transactionChanges={cashTransactionChanges}
                      onTransactionChange={handleCashTransactionChange}
                      onViewRecord={setSelectedCashRecordId}
                    />
                  </>
                ),
            }))}
            defaultTab={PostUnrealisedFXGainsTab.PayablesOrReceivables}
          />
        )}
      </Column>
      <ConfirmDialog
        open={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Approve and create transactions?"
        content={`This will post ${totalTransactionCount} ${pluralize(
          'transaction set',
          totalTransactionCount,
        )} to the GL and mark the checklist item as reviewed.`}
        actions={{
          confirm: {
            onClick: handleApproveConfirm,
            label: 'Approve and create transactions',
          },
        }}
      />
      <ConfirmDialog
        open={isUnapproveModalOpen}
        onClose={() => setIsUnapproveModalOpen(false)}
        title="Unapprove and delete transactions?"
        content={`This will remove ${totalTransactionCount} ${pluralize(
          'transaction set',
          totalTransactionCount,
        )} from the GL and mark the checklist item as unreviewed.`}
        actions={{
          confirm: {
            onClick: handleUnapprove,
            label: 'Unapprove and delete transactions',
            color: 'error',
          },
        }}
      />
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={() => void handleApprove()}
          loading={isPending}
        />
      )}
    </FullPageDrawer>
  );
};

const PostUnrealisedFXGains: FC<Props> = ({ onExit }) => (
  <PostUnrealisedFxGainsProvider>
    <PostUnrealisedFXGainsContent onExit={onExit} />
  </PostUnrealisedFxGainsProvider>
);

export default PostUnrealisedFXGains;
